import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { connect } from 'react-redux';
import _find from 'lodash/find';
import _get from 'lodash/get';
import _isString from 'lodash/isString';
import DraggableLayer from './DraggableLayer';
import TextLayer from './TextLayer';

import FeatureFlags from '../../../../util/featureFlags';
// Actions

import { actions as projectActions } from '../../../actions/projectActions';
// Selector

import { getProjectMedium } from '../../../selectors/cardProjectSelector';
// Util

import { pxToPt } from '../../../util';
// Stylesheets
import './TextLayer.less';

class DraggableTextLayer extends React.Component {
  constructor(props) {
    super(props);
    this.handleDragStop = this.handleDragStop.bind(this);
  }

  handleDragStop(deltas) {
    const { textElement, customizationValues, handleChange } = this.props;

    // if all deltas are 0 then no change has occured, dont pollute change history
    // with changes that dont result in different state
    if (!(deltas.top === 0 && deltas.left === 0 && deltas.right === 0 && deltas.bottom === 0)) {
      // group changes in an array so that undo functionality knows to undo each field changed
      // at the same time, this is especially important for moving/resizing because the changes
      // only make sense when grouped together - dont want users undoing one change without undoing
      // the others. For example, if an element was dragged horizontally, we want to be able to undo both
      // the x and y position changes at the same time, otherwise user would have to click 'undo' twice before
      // getting the element back to the original position
      let changes = [
        {
          elementUUID: textElement.uuid,
          fieldName: 'x',
          value: textElement.x + pxToPt(deltas.left),
        },
        {
          elementUUID: textElement.uuid,
          fieldName: 'y',
          value: textElement.y + pxToPt(deltas.top),
        },
        {
          elementUUID: textElement.uuid,
          fieldName: 'width',
          value: textElement.width - pxToPt(deltas.right) - pxToPt(deltas.left),
        },
        {
          elementUUID: textElement.uuid,
          fieldName: 'height',
          value: textElement.height - pxToPt(deltas.bottom) - pxToPt(deltas.top),
        },
      ];
      // for text elements like rsvp/meal options where there is a bullet point image next to the line,
      // make sure to also update the position of that linked image element with the new coordinates
      // so that user does not have to drag both text and image elements and align them again
      const linkedElement = _find(customizationValues, { linked_element_uuid: textElement.uuid });
      if (linkedElement) {
        // we need to account for moving linked elements that are positioned either to the right
        // or to the left of the text element - to do this we just grab the x values of both elements
        // and determine which is greater
        let linkedElementDeltaX = 0;
        if (textElement.x > linkedElement.x) {
          // if the textElement's x value is greater, that means the linkedElement is to the left
          // of the textElement - so we want to shift it whenever the left positioning of the textElement changes
          linkedElementDeltaX = deltas.left;
        } else if (textElement.x < linkedElement.x) {
          // if the linkedElements x value is greater, that means the linkedElement is to the right
          // of the textElement - shift based on the inverse of the change in the right positioning
          // NB: needs to be inversed because deltas.right will be positive when the element has moved left
          // and negative when the value has moved right, so if deltas.right is positive then we need to change
          // the x value of the linked element by -(deltas.right)
          linkedElementDeltaX = -deltas.right;
        }
        // no need to update width/height of image elements, only update based on top/left deltas of text element
        // if the text element is resized to the bottom/right the linked element position does not get updated
        changes = [
          ...changes,
          {
            elementUUID: linkedElement.uuid,
            fieldName: 'x',
            value: linkedElement.x + pxToPt(linkedElementDeltaX),
          },
          {
            elementUUID: linkedElement.uuid,
            fieldName: 'y',
            value: linkedElement.y + pxToPt(deltas.top),
          },
        ];
      }
      handleChange(changes);
    }
  }

  render() {
    const {
      isFoldable,
      textElement,
      editable,
      isActive,
      parentDimensions,
      handleTextBlur,
      handleTextChange,
      medium,
      customizationFoilColor,
      tag,
      shouldBlurElement,
      isEnvelopeStepRecipientAddressEditable,
      outlineOnHover,
      isPostcardReviewStep,
    } = this.props;
    const { body, x, y, width, height } = textElement;
    const needToAdjustFontTop =
      FeatureFlags.get('fontRenderFix') &&
      _get(this.props, 'textElement.font.font_family') === 'Caslon Graphique D';
    const adjustFontTop = needToAdjustFontTop ? 2 : 0;

    // position and z-index of text element on the page need to be set here - would like to have this
    // in TextLayer so that there is no dependency on DraggableTextLayer but cant think of another way
    const heightOffset =
      isFoldable && parentDimensions.height > 315
        ? parentDimensions.height / 2
        : parentDimensions.height;
    const outerTextLayerStyles = {
      position: 'absolute',
      top: `${(isFoldable ? y - heightOffset : y) + adjustFontTop}pt`,
      left: `${x}pt`,
      width: `${width}pt`,
      height: `${height}pt`,
      zIndex: isActive ? 2000 : Math.max(Math.floor(y), 10),
      filter: shouldBlurElement ? 'blur(2px)' : 'none',
    };
    const testLayerStyle = cx('text-layer-container', {
      recipient: tag && tag === 'RECIPIENT_ADDRESS' && FeatureFlags.get('envelopeGuestAddressing'),
      postcard: isPostcardReviewStep,
    });
    // not sure if check for isString is needed, but should help to ensure that
    // elementRef prop that is passed to DraggableLayer does not cause an error
    if (_isString(body)) {
      return (
        <DraggableLayer
          element={textElement}
          style={outerTextLayerStyles}
          className={testLayerStyle}
          isActive={isActive}
          parentDimensions={parentDimensions}
          dragStopCallback={this.handleDragStop}
          disabled={!editable || tag === 'RECIPIENT_ADDRESS'}
          elementRef={this.textLayerRef && this.textLayerRef.textElement}
          isFoldable={isFoldable}
        >
          {(deltas, dragging, dragHandle) => (
            <TextLayer
              {...this.props}
              deltas={deltas}
              dragging={dragging}
              dragHandle={dragHandle}
              handleTextBlur={handleTextBlur}
              handleTextChange={handleTextChange}
              ref={el => {
                this.textLayerRef = el;
              }}
              medium={medium}
              foilColor={customizationFoilColor}
              isEnvelopeStepRecipientAddressEditable={isEnvelopeStepRecipientAddressEditable}
              outlineOnHover={outlineOnHover}
              blurred={shouldBlurElement}
            />
          )}
        </DraggableLayer>
      );
    }
    return null;
  }
}

DraggableTextLayer.propTypes = {
  textElement: PropTypes.shape({}),
  customizationValues: PropTypes.shape({}),
  isActive: PropTypes.bool,
  editable: PropTypes.bool,
  boundingBoxesAdjusted: PropTypes.bool,
  parentDimensions: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  onSetFieldWidthError: PropTypes.func,
  onRemoveFieldWidthError: PropTypes.func,
  onSetElementHeightError: PropTypes.func,
  onRemoveElementHeightError: PropTypes.func,
  handleChange: PropTypes.func,
  handleTextBlur: PropTypes.func,
  handleTextChange: PropTypes.func,
  isNewElement: PropTypes.bool,
  customizationFoilColor: PropTypes.string,
};

const mapStateToProps = state => ({
  medium: getProjectMedium(state),
});
// TODO: maybe move this down to TextLayer
const mapDispatchToProps = dispatch => ({
  setActiveField: name => dispatch(projectActions.setActiveField(name)),
});

export default connect(mapStateToProps, mapDispatchToProps)(DraggableTextLayer);
